import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ArrowRight, CheckCircle2, Loader2, ReceiptText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useMyUser } from '@/hooks/use-user';
import { api } from '@/lib/api';
import type { SubscriptionCheckoutConfirmation } from '@/types/subscription';

interface SummaryRowProps {
  label: string;
  value: string;
}

function formatCurrency(amountInCents: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amountInCents / 100);
}

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs text-night-900 sm:text-sm">
      <span className="font-body text-xs text-night-700 sm:text-sm">{label}</span>
      <span className="font-display text-base font-medium text-night-900 sm:text-lg">{value}</span>
    </div>
  );
}

export function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const checkoutSessionPath = sessionId ? `/api/v1/me/subscription/checkout/${sessionId}` : null;
  const { user } = useMyUser();

  const {
    data: checkoutConfirmation,
    error,
    isLoading,
  } = useSWR<SubscriptionCheckoutConfirmation>(
    checkoutSessionPath,
    (path: string) => api.get(path),
    {
      revalidateOnFocus: false,
    }
  );

  const currency = checkoutConfirmation?.currency || 'USD';
  const planAmount = checkoutConfirmation
    ? formatCurrency(checkoutConfirmation.planAmount, currency)
    : null;
  const discountAmount =
    checkoutConfirmation && checkoutConfirmation.discountAmount > 0
      ? formatCurrency(checkoutConfirmation.discountAmount, currency)
      : null;
  const totalAmount = checkoutConfirmation
    ? formatCurrency(checkoutConfirmation.amountTotal, currency)
    : null;
  const discountLabel = checkoutConfirmation?.promoCode
    ? `Promo Code (${checkoutConfirmation.promoCode})`
    : 'Discount';
  const showFallbackSummary = !sessionId || Boolean(error);
  const isSupervisorUser = user?.userRole === 'SUPERVISOR';
  const successCtaPath = isSupervisorUser ? '/dashboard/caregivers' : '/dashboard/settings';
  const successCtaLabel = isSupervisorUser
    ? user?.supervisorType === 'GUARDIAN'
      ? 'Manage Teen Caregivers'
      : 'Manage Caregivers'
    : 'Complete Profile';

  return (
    <div className="bg-linen min-h-dvh overflow-hidden px-4 py-4 sm:px-6 sm:py-6">
      <div className="mx-auto flex min-h-[calc(100dvh-2rem)] max-w-3xl items-center justify-center sm:min-h-[calc(100dvh-3rem)]">
        <div className="w-full overflow-hidden rounded-3xl border border-lilac-900 bg-white shadow-sm hover:shadow-md transition-shadow">
          <div className="px-5 py-6 sm:px-8 sm:py-8">
            <div className="mx-auto max-w-2xl text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-lilac-900 text-blueviolet">
                <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
              </div>
              <h1 className="font-display text-[1.9rem] font-semibold tracking-tight text-night-900 sm:text-[2.4rem]">
                Welcome to CareCal!
              </h1>
              <p className="mt-2 font-display text-lg text-night-700 sm:text-xl">
                Your plan is now active.
              </p>
              <p className="mx-auto mt-4 max-w-xl font-body text-sm leading-6 text-night-700 sm:text-base">
                Your payment method has been charged for the membership plan you selected. Your
                selected plan will auto-renew until canceled.
              </p>
            </div>

            <div className="mx-auto mt-6 max-w-2xl">
              <Card className="rounded-[1.5rem] border border-night-100 bg-white p-4 shadow-none transition-none hover:shadow-none sm:p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blueviolet shadow-sm">
                    <ReceiptText className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-gray-900 sm:text-xl">
                      Payment Summary
                    </h2>
                  </div>
                </div>

                {isLoading && sessionId ? (
                  <div className="flex min-h-40 flex-col items-center justify-center gap-3 py-6 text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blueviolet" />
                    <p className="font-body text-sm text-night-500 sm:text-base">
                      Finalizing your payment summary...
                    </p>
                  </div>
                ) : null}

                {checkoutConfirmation ? (
                  <div className="mt-4 space-y-3">
                    <SummaryRow
                      label={checkoutConfirmation.planDisplayName}
                      value={planAmount || ''}
                    />
                    {discountAmount ? (
                      <SummaryRow label={discountLabel} value={`-${discountAmount}`} />
                    ) : null}
                    <div className="my-3 h-px bg-night-300" />
                    <div className="flex justify-end">
                      <div className="flex flex-wrap items-baseline justify-end gap-x-3 gap-y-1 text-right">
                        <span className="font-display text-sm font-medium text-night-900 sm:text-base">
                          Order Total:
                        </span>
                        <span className="font-display text-base font-semibold text-night-900 sm:text-lg">
                          {totalAmount}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {showFallbackSummary ? (
                  <div className="mt-6 rounded-[1.25rem] border border-night-300 p-4 text-left">
                    <p className="font-body text-sm leading-6 text-night-700 sm:text-base">
                      Your subscription is active. We could not load the detailed Stripe summary for
                      this page, but your account is ready to use.
                    </p>
                    {!sessionId ? (
                      <p className="mt-2 font-body text-xs text-night-500 sm:text-sm">
                        This confirmation link did not include a checkout session ID.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </Card>
            </div>

            <div className="mx-auto mt-6 flex max-w-2xl justify-center">
              <Button asChild size="lg" className="w-full max-w-md">
                <Link to={successCtaPath}>
                  {successCtaLabel}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
